import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import bettingService from '../../services/bettingAPI';
import adminAPI from '../../services/adminAPI';
import toast from 'react-hot-toast';
import '../../styles/admin/CurrentBets.css';
import { useDesktopViewport } from '../../hooks/useDesktopViewport';

const CurrentBets = () => {
    useDesktopViewport();
    // ---- Get user from localStorage to check role ----
    const getUserFromStorage = () => {
        try {
            return JSON.parse(localStorage.getItem('user') || '{}');
        } catch {
            return {};
        }
    };
    const userData = getUserFromStorage();
    const isAdmin = userData?.role === 'admin';   // ← boolean for access control

    // ---- Rest of the component ----
    const { user } = useAuth(); // kept if needed elsewhere
    const [bets, setBets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [settlingBets, setSettlingBets] = useState(new Set());
    const [showSettleModal, setShowSettleModal] = useState(false);
    const [selectedBet, setSelectedBet] = useState(null);
    const [settleValues, setSettleValues] = useState({
        actualResult: '',
        settleResult: 'win'
    });
    const [filters, setFilters] = useState({
        marketType: 'all',
        betSubType: 'all',
        searchUser: ''
    });

    useEffect(() => {
        loadBets();
    }, [filters]);

    const loadBets = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getAllBets({
                marketType: filters.marketType,
                betSubType: filters.betSubType,
                search: filters.searchUser,
                status: 'unsettled',
                limit: 10000
            });
            setBets(response.data.bets);
        } catch (error) {
            console.error('Error loading bets:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters({ ...filters, [key]: value });
    };

    const handleSearch = () => {
        loadBets();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const openSettleModal = (bet) => {
        const isSessionBet = bet.marketType === 'session';
        setSelectedBet(bet);
        setSettleValues({
            actualResult: isSessionBet ? '' : '0',
            settleResult: 'win'
        });
        setShowSettleModal(true);
    };

    const quickSettleBookmaker = async (bet, isWin) => {
        const action = isWin ? 'WIN' : 'LOSS';
        if (!confirm(`Confirm: Mark this bet as ${action}?\n\nUser: ${bet.username}\nMatch: ${bet.matchName || 'N/A'}\nSelection: ${bet.selectionName}\nStake: ₹${bet.stake}\n${isWin ? 'Will credit: ₹' + bet.potentialWin : 'Will deduct: ₹' + bet.exposureAmount}`)) {
            return;
        }

        setSettlingBets(prev => new Set(prev).add(bet._id));

        try {
            const response = await adminAPI.settleBetManual({
                betId: bet.betId,
                actualResult: 0,
                isWin: isWin
            });

            if (response.success) {
                toast.success(`Bet ${bet.betId} settled as ${action}`);
                await loadBets();
            } else {
                toast.error(response.message || 'Failed to settle bet');
            }
        } catch (error) {
            toast.error('Failed to settle bet: ' + (error.response?.data?.message || error.message));
        } finally {
            setSettlingBets(prev => {
                const newSet = new Set(prev);
                newSet.delete(bet._id);
                return newSet;
            });
        }
    };

    const handleSettleSessionBet = async () => {
        if (!selectedBet) return;
        
        if (!settleValues.actualResult || settleValues.actualResult === '') {
            toast.error('Please enter the actual runs/score');
            return;
        }

        const actualResult = parseFloat(settleValues.actualResult);
        if (isNaN(actualResult)) {
            toast.error('Please enter a valid number');
            return;
        }

        setSettlingBets(prev => new Set(prev).add(selectedBet._id));

        try {
            const isWin = settleValues.settleResult === 'win';
            
            const response = await adminAPI.settleBetManual({
                betId: selectedBet.betId,
                actualResult: actualResult,
                isWin: isWin
            });

            if (response.success) {
                toast.success(`Session bet settled as ${isWin ? 'WIN' : 'LOSS'} with ${actualResult} runs`);
                await loadBets();
                setShowSettleModal(false);
                setSelectedBet(null);
            } else {
                toast.error(response.message || 'Failed to settle bet');
            }
        } catch (error) {
            toast.error('Failed to settle bet: ' + (error.response?.data?.message || error.message));
        } finally {
            setSettlingBets(prev => {
                const newSet = new Set(prev);
                newSet.delete(selectedBet._id);
                return newSet;
            });
        }
    };

    const handleVoidBet = async (bet) => {
        if (!confirm(`VOID this bet?\n\nUser: ${bet.username}\nMatch: ${bet.matchName || 'N/A'}\nSelection: ${bet.selectionName}\nStake: ₹${bet.stake}\n\nThe stake of ₹${bet.stake} will be returned to user.`)) {
            return;
        }

        setSettlingBets(prev => new Set(prev).add(bet._id));

        try {
            const response = await adminAPI.settleBetManual({
                betId: bet.betId,
                actualResult: 0,
                isWin: false,
                markAsVoid: true
            });

            if (response.success) {
                toast.success(`Bet voided - ₹${bet.stake} returned to user`);
                await loadBets();
            } else {
                toast.error(response.message || 'Failed to void bet');
            }
        } catch (error) {
            toast.error('Failed to void bet');
        } finally {
            setSettlingBets(prev => {
                const newSet = new Set(prev);
                newSet.delete(bet._id);
                return newSet;
            });
        }
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '₹0';
        return `₹${amount.toLocaleString('en-IN')}`;
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getBetTypeDisplay = (bet) => {
        if (bet.marketType === 'session') {
            return bet.betType === 'yes' ? 'YES' : 'NO';
        }
        return bet.betType === 'back' ? 'BACK' : 'LAY';
    };

    const getRowColorClass = (bet) => {
        if (bet.marketType === 'session') {
            return bet.betType === 'yes' ? 'row-yes' : 'row-no';
        }
        return bet.betType === 'back' ? 'row-back' : 'row-lay';
    };

    const getMarketTypeIcon = (type) => {
        const icons = {
            bookmaker: '🏏',
            bookmaker2: '🎯',
            bookmaker_tied: '🤝',
            session: '📊'
        };
        return icons[type] || '🎲';
    };

    const getBetTypeClass = (bet) => {
        if (bet.marketType === 'session') {
            return bet.betType === 'yes' ? 'bet-yes' : 'bet-no';
        }
        return bet.betType === 'back' ? 'bet-back' : 'bet-lay';
    };

    const isSessionBet = (bet) => bet.marketType === 'session';
    const isBookmakerBet = (bet) => ['bookmaker', 'bookmaker2', 'bookmaker_tied'].includes(bet.marketType);

    const totals = bets.reduce((acc, bet) => {
        acc.totalStake += bet.stake || 0;
        acc.totalPotentialWin += bet.potentialWin || 0;
        acc.totalExposure += bet.exposureAmount || 0;
        return acc;
    }, { totalStake: 0, totalPotentialWin: 0, totalExposure: 0 });

    return (
        <div className="current-bets">
            {/* Filter Bar – unchanged */}
            <div className="filter-bar">
                <div className="filter-group">
                    <label>Market Type</label>
                    <select value={filters.marketType} onChange={(e) => handleFilterChange('marketType', e.target.value)}>
                        <option value="all">All Markets</option>
                        <option value="bookmaker">Bookmaker</option>
                        <option value="bookmaker2">Bookmaker 2</option>
                        <option value="bookmaker_tied">Bookmaker Tied</option>
                        <option value="session">Session</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Bet Type</label>
                    <select value={filters.betSubType} onChange={(e) => handleFilterChange('betSubType', e.target.value)}>
                        <option value="all">All</option>
                        {filters.marketType === 'session' ? (
                            <>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </>
                        ) : (
                            <>
                                <option value="back">Back</option>
                                <option value="lay">Lay</option>
                            </>
                        )}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Search by Username</label>
                    <input
                        type="text"
                        placeholder="Enter username..."
                        value={filters.searchUser}
                        onChange={(e) => handleFilterChange('searchUser', e.target.value)}
                        onKeyPress={handleKeyPress}
                    />
                </div>

                <div className="filter-actions">
                    <button className="load-btn" onClick={handleSearch}>🔍 Search</button>
                </div>
            </div>

            {/* Bets Table */}
<div className="table-wrapper">
  {loading ? (
    <div className="loading">Loading bets...</div>
  ) : (
    <table className="bets-tablex">
      <thead>
        <tr>
          <th>#</th>
          <th>Event Type</th>
          <th>Event Name</th>
          <th>User</th>
          <th>M Name</th>
          <th>Nation</th>
          <th>Type</th>
          <th>Odds</th>
          <th>Stake</th>
          <th>Win</th>
          <th>Exposure</th>
          <th>Place Time</th>
          {isAdmin && <th>Actions</th>}   {/* ← only show for admin */}
        </tr>
      </thead>
      <tbody>
        {bets.map((bet, index) => (
          <tr key={bet._id} className={getRowColorClass(bet)}>
            <td className="serial-number">{index + 1}</td>
            <td>Cricket</td>
            <td className="match-name-cell">
              <span className="match-name-badge">
                {bet.matchName || 'N/A'}
              </span>
            </td>
            <td>
              <div className="user-cell">
                <span className="username">{bet.username}</span>
              </div>
            </td>
            <td>
              <span>{bet.marketName}</span>
            </td>
            <td className="selection-name">{bet.selectionName}</td>
            <td>
              <span className={`bet-type-badge ${getBetTypeClass(bet)}`}>
                {getBetTypeDisplay(bet)}
              </span>
            </td>
            <td className="odds">{bet.odds}</td>
            <td className="stake">{formatCurrency(bet.stake)}</td>
            <td className="potential-win">{formatCurrency(bet.potentialWin)}</td>
            <td className="exposure">{formatCurrency(bet.exposureAmount)}</td>
            <td className="place-time">{formatDate(bet.createdAt)}</td>

            {/* Actions column – only rendered for admin */}
            {isAdmin && (
              <td className="actions-cell">
                {isBookmakerBet(bet) ? (
                  <div className="action-btns">
                    <button
                      className="btn-win"
                      onClick={() => quickSettleBookmaker(bet, true)}
                      disabled={settlingBets.has(bet._id)}
                      title="Mark as WIN"
                    >
                      {settlingBets.has(bet._id) ? '⏳' : '✅ WIN'}
                    </button>
                    <button
                      className="btn-loss"
                      onClick={() => quickSettleBookmaker(bet, false)}
                      disabled={settlingBets.has(bet._id)}
                      title="Mark as LOSS"
                    >
                      {settlingBets.has(bet._id) ? '⏳' : '❌ LOSS'}
                    </button>
                    <button
                      className="btn-void-small"
                      onClick={() => handleVoidBet(bet)}
                      disabled={settlingBets.has(bet._id)}
                      title="Void Bet"
                    >
                      🚫
                    </button>
                  </div>
                ) : (
                  <div className="action-btns">
                    <button
                      className="btn-settle-session"
                      onClick={() => openSettleModal(bet)}
                      disabled={settlingBets.has(bet._id)}
                    >
                      {settlingBets.has(bet._id) ? '⏳' : '💰 Settle'}
                    </button>
                    <button
                      className="btn-void-small"
                      onClick={() => handleVoidBet(bet)}
                      disabled={settlingBets.has(bet._id)}
                    >
                      🚫
                    </button>
                  </div>
                )}
              </td>
            )}
          </tr>
        ))}
        {bets.length === 0 && !loading && (
          <tr>
            <td colSpan={isAdmin ? 13 : 12} className="text-center">
              No unsettled bets found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  )}
</div>

            {/* Settlement Modal - FOR SESSION BETS ONLY */}
            {showSettleModal && selectedBet && (
                <div className="modal-overlay" onClick={() => setShowSettleModal(false)}>
                    <div className="settle-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header" style={{
                            background: selectedBet.betType === 'yes' 
                                ? 'linear-gradient(135deg, #1B5E20, #2E7D32)' 
                                : 'linear-gradient(135deg, #B71C1C, #C62828)'
                        }}>
                            <h3>💰 Settle Session Bet</h3>
                            <button className="close-btn" onClick={() => setShowSettleModal(false)}>✕</button>
                        </div>
                        
                        <div className="modal-body">
                            {/* Bet Info Card */}
                            <div className="settle-bet-card" style={{
                                borderLeft: `4px solid ${selectedBet.betType === 'yes' ? '#2E7D32' : '#C62828'}`,
                                background: selectedBet.betType === 'yes' ? '#f1f8e9' : '#fce4ec'
                            }}>
                                <div className="card-row">
                                    <span className="card-label">User</span>
                                    <span className="card-value">{selectedBet.username}</span>
                                </div>
                                <div className="card-row">
                                    <span className="card-label">Match</span>
                                    <span className="card-value highlight"> {selectedBet.matchName || 'N/A'}</span>
                                </div>
                                <div className="card-row">
                                    <span className="card-label">Market</span>
                                    <span className="card-value">{selectedBet.marketName}</span>
                                </div>
                                <div className="card-row">
                                    <span className="card-label">Selection</span>
                                    <span className="card-value highlight">{selectedBet.selectionName}</span>
                                </div>
                                <div className="card-row">
                                    <span className="card-label">Bet Type</span>
                                    <span className={`bet-type-badge ${getBetTypeClass(selectedBet)}`}>
                                        {getBetTypeDisplay(selectedBet)}
                                    </span>
                                </div>
                                <div className="card-row">
                                    <span className="card-label">Odds / Stake</span>
                                    <span className="card-value">{selectedBet.odds} / {formatCurrency(selectedBet.stake)}</span>
                                </div>
                                <div className="card-row">
                                    <span className="card-label">Potential Win</span>
                                    <span className="card-value win-color">{formatCurrency(selectedBet.potentialWin)}</span>
                                </div>
                                <div className="card-row">
                                    <span className="card-label">Exposure</span>
                                    <span className="card-value loss-color">{formatCurrency(selectedBet.exposureAmount)}</span>
                                </div>
                            </div>

                            {/* Settlement Form */}
                            <div className="settle-form">
                                <div className="form-group">
                                    <label>🏏 Actual Runs Scored</label>
                                    <input
                                        type="number"
                                        placeholder="Enter actual runs/score..."
                                        value={settleValues.actualResult}
                                        onChange={(e) => setSettleValues({...settleValues, actualResult: e.target.value})}
                                        autoFocus
                                        style={{
                                            border: '2px solid #1976D2',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            fontSize: '16px',
                                            width: '100%'
                                        }}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Settlement Result</label>
                                    <div className="result-toggle">
                                        <button 
                                            className={`toggle-btn win ${settleValues.settleResult === 'win' ? 'active' : ''}`}
                                            onClick={() => setSettleValues({...settleValues, settleResult: 'win'})}
                                        >
                                            ✅ WIN
                                        </button>
                                        <button 
                                            className={`toggle-btn loss ${settleValues.settleResult === 'loss' ? 'active' : ''}`}
                                            onClick={() => setSettleValues({...settleValues, settleResult: 'loss'})}
                                        >
                                            ❌ LOSS
                                        </button>
                                    </div>
                                </div>

                                {/* Settlement Preview */}
                                <div className="settle-preview" style={{
                                    background: settleValues.settleResult === 'win' 
                                        ? 'linear-gradient(135deg, #E8F5E9, #C8E6C9)' 
                                        : 'linear-gradient(135deg, #FFEBEE, #FFCDD2)',
                                    borderRadius: '10px',
                                    padding: '15px',
                                    marginTop: '15px',
                                    textAlign: 'center'
                                }}>
                                    {settleValues.settleResult === 'win' ? (
                                        <div style={{ color: '#2E7D32' }}>
                                            <div style={{ fontSize: '14px', marginBottom: '5px' }}>🏆 WIN - Amount to Credit</div>
                                            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                                                {formatCurrency(selectedBet.potentialWin)}
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ color: '#C62828' }}>
                                            <div style={{ fontSize: '14px', marginBottom: '5px' }}>💸 LOSS - Amount to Deduct</div>
                                            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                                                {formatCurrency(selectedBet.exposureAmount)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={() => setShowSettleModal(false)}>
                                Cancel
                            </button>
                            <button 
                                className="confirm-settle-btn"
                                onClick={handleSettleSessionBet}
                                disabled={settlingBets.has(selectedBet._id) || !settleValues.actualResult}
                                style={{
                                    background: settleValues.settleResult === 'win' 
                                        ? 'linear-gradient(135deg, #2E7D32, #43A047)' 
                                        : 'linear-gradient(135deg, #C62828, #E53935)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '12px 30px',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                }}
                            >
                                {settlingBets.has(selectedBet._id) ? '⏳ Settling...' : `Confirm ${settleValues.settleResult.toUpperCase()}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CurrentBets;